import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { legalRegisterApi, peopleApi } from '../services/api';
import { LinkedArtefacts } from '../components/LinkedArtefacts';
import { useToast } from '../hooks/useToast';
import { PageHeader } from '../components/PageHeader';

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

type LegalRequirement = {
  lawId: string;
  lawName: string;
  jurisdiction?: string;
  howItApplies?: string;
  contactPersonId?: string;
  contactPersonName?: string;
  reviewDate?: string;
  status?: string;
  notes?: string;
};

const EMPTY_FORM = {
  lawName: '',
  jurisdiction: '',
  howItApplies: '',
  contactPersonId: '',
  contactPersonName: '',
  reviewDate: '',
  status: 'Not Assessed',
  notes: '',
};

const JURISDICTIONS = [
  'EU', 'UK', 'Ireland', 'USA', 'Canada', 'Australia', 'International', 'Other',
];

const STATUSES = [
  'Compliant', 'Under Review', 'Non-Compliant', 'Not Assessed', 'Exempt',
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LegalRequirementFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [activeTab, setActiveTab] = useState<'details' | 'artefacts'>('details');

  // Person search state
  const [personSearch, setPersonSearch] = useState('');
  const [showPersonDrop, setShowPersonDrop] = useState(false);
  const personInputRef = useRef<HTMLInputElement>(null);

  const { data: people = [] } = useQuery<any[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  // Load existing item when editing
  const { data: itemData, isLoading: loadingItem } = useQuery<any>({
    queryKey: ['legalRequirement', id],
    queryFn: () => legalRegisterApi.getOne(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (itemData) {
      const item: LegalRequirement = itemData.legalRequirement ?? itemData;
      setForm({
        lawName: item.lawName || '',
        jurisdiction: item.jurisdiction || '',
        howItApplies: item.howItApplies || '',
        contactPersonId: item.contactPersonId || '',
        contactPersonName: item.contactPersonName || '',
        reviewDate: item.reviewDate || '',
        status: item.status || 'Not Assessed',
        notes: item.notes || '',
      });
      setPersonSearch(item.contactPersonName || '');
    }
  }, [itemData]);

  const setField = (key: keyof typeof EMPTY_FORM, value: any) =>
    setForm((f) => ({ ...f, [key]: value }));

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        await legalRegisterApi.update(id!, form);
      } else {
        await legalRegisterApi.create(form);
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['legal-register'] });
      toast.success(isEdit ? 'Requirement updated.' : 'Requirement added.');
      navigate('/legal-register');
    },
    onError: (e: Error) => toast.error(`Failed to save: ${e.message}`),
  });

  if (isEdit && loadingItem) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-scales-3-line"
        title={isEdit ? 'Edit Legal Requirement' : 'Add Legal Requirement'}
        colour="danger"
      />

      {/* Tab navigation */}
      <div className="np-tabs">
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'details' ? ' active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          <i className="ri-scales-3-line"></i>Requirement Details
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'artefacts' ? ' active' : ''}`}
          onClick={() => setActiveTab('artefacts')}
        >
          <i className="ri-links-line"></i>Linked Artefacts
        </button>
      </div>

      {/* ── DETAILS TAB ── */}
      {activeTab === 'details' && (
        <>
          {/* Core fields */}
          <div className="np-card mb-4">
            <div className="np-card-head">
              <h2><i className="ri-file-text-line text-danger"></i> Law / Regulation</h2>
            </div>
            <div className="np-card-body">
              <div className="row g-3">
                <div className="col-md-8 np-field">
                  <label>
                    Law / Requirement Name <span className="text-danger">*</span>
                  </label>
                  <input
                    className="np-input"
                    value={form.lawName}
                    onChange={(e) => setField('lawName', e.target.value)}
                    placeholder="e.g. GDPR, NIS2 Directive, Health & Safety Act"
                  />
                </div>

                <div className="col-md-4 np-field">
                  <label>Jurisdiction</label>
                  <select
                    className="np-select"
                    value={form.jurisdiction}
                    onChange={(e) => setField('jurisdiction', e.target.value)}
                  >
                    <option value="">— Select —</option>
                    {JURISDICTIONS.map((j) => <option key={j}>{j}</option>)}
                  </select>
                </div>

                <div className="col-12 np-field">
                  <label>How it Applies to Us</label>
                  <textarea
                    className="np-input"
                    rows={4}
                    value={form.howItApplies}
                    onChange={(e) => setField('howItApplies', e.target.value)}
                    placeholder="Brief note on how this law or regulation applies to the organisation"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Compliance fields */}
          <div className="np-card mb-4">
            <div className="np-card-head">
              <h2><i className="ri-shield-check-line text-warning"></i> Compliance &amp; Assignment</h2>
            </div>
            <div className="np-card-body">
              <div className="row g-3">
                {/* Contact person with typeahead */}
                <div className="col-md-5 np-field" style={{ position: 'relative' }}>
                  <label>Contact Person</label>
                  <input
                    ref={personInputRef}
                    className="np-input"
                    placeholder="Search by name or job title…"
                    value={personSearch}
                    autoComplete="off"
                    onChange={(e) => {
                      setPersonSearch(e.target.value);
                      setShowPersonDrop(true);
                      if (!e.target.value) {
                        setForm((f) => ({ ...f, contactPersonId: '', contactPersonName: '' }));
                      }
                    }}
                    onFocus={() => setShowPersonDrop(true)}
                    onBlur={() => setTimeout(() => setShowPersonDrop(false), 150)}
                  />
                  {showPersonDrop && (() => {
                    const q = personSearch.toLowerCase();
                    const matches = people
                      .filter((p) =>
                        !q ||
                        `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase().includes(q) ||
                        (p.jobTitle || '').toLowerCase().includes(q),
                      )
                      .slice(0, 8);
                    if (matches.length === 0) return null;
                    return (
                      <div
                        className="np-popover"
                        style={{ right: 0, maxHeight: 220, overflowY: 'auto' }}
                      >
                        {matches.map((p) => {
                          const fullName =
                            `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email || p.personId;
                          return (
                            <div
                              key={p.personId}
                              className="np-popover-item"
                              onMouseDown={() => {
                                setForm((f) => ({ ...f, contactPersonId: p.personId, contactPersonName: fullName }));
                                setPersonSearch(fullName);
                                setShowPersonDrop(false);
                              }}
                            >
                              <div className="avatar avatar-xs flex-shrink-0">
                                <span
                                  className="avatar-initial rounded-circle bg-label-primary"
                                  style={{ fontSize: '0.65rem' }}
                                >
                                  {(fullName[0] || '?').toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="small fw-semibold lh-1">{fullName}</div>
                                {(p.jobTitle || p.departmentName || p.department) && (
                                  <div className="np-hint mb-0">
                                    {[p.jobTitle, p.departmentName || p.department]
                                      .filter(Boolean)
                                      .join(' · ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                <div className="col-md-3 np-field">
                  <label>Review Date</label>
                  <input
                    type="date"
                    className="np-input"
                    value={form.reviewDate}
                    onChange={(e) => setField('reviewDate', e.target.value)}
                  />
                </div>

                <div className="col-md-4 np-field">
                  <label>Status</label>
                  <select
                    className="np-select"
                    value={form.status}
                    onChange={(e) => setField('status', e.target.value)}
                  >
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>

                <div className="col-12 np-field">
                  <label>Notes</label>
                  <textarea
                    className="np-input"
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setField('notes', e.target.value)}
                    placeholder="Additional notes or context"
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── LINKED ARTEFACTS TAB ── */}
      {activeTab === 'artefacts' && (
        <div className="np-card mb-4">
          <div className="np-card-head">
            <h2><i className="ri-links-line text-primary"></i> Linked Artefacts</h2>
          </div>
          <div className="np-card-body">
            {isEdit ? (
              <>
                <p className="np-hint mb-3">
                  <i className="ri-information-line me-1"></i>
                  Link risks, controls, physical sites, or other artefacts to this legal requirement. Useful for
                  mapping which parts of the organisation are affected by a law or regulation.
                </p>
                <LinkedArtefacts
                  artefactId={id!}
                  artefactType="legal_requirement"
                  artefactRegister="legal-register"
                />
              </>
            ) : (
              <div className="text-center py-5 np-hint">
                <i className="ri-links-line ri-3x d-block mb-3 opacity-25"></i>
                <p className="mb-1 fw-semibold">Linked Artefacts not yet available</p>
                <p className="small mb-0">Save this requirement first, then come back to link risks, controls, physical sites and other artefacts.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Save / Cancel */}
      {activeTab === 'details' && (
        <div className="d-flex gap-2 justify-content-end">
          <button
            type="button"
            className="np-btn np-btn-ghost"
            onClick={() => navigate('/legal-register')}
          >
            Cancel
          </button>
          <button
            type="button"
            className="np-btn np-btn-primary"
            disabled={!form.lawName.trim() || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? (
              <><span className="spinner-border spinner-border-sm me-1"></span>Saving…</>
            ) : isEdit ? (
              'Save Changes'
            ) : (
              'Add Requirement'
            )}
          </button>
        </div>
      )}

      {activeTab === 'artefacts' && (
        <div className="d-flex gap-2 justify-content-end">
          <button
            type="button"
            className="np-btn np-btn-ghost"
            onClick={() => navigate('/legal-register')}
          >
            Back to Legal Register
          </button>
        </div>
      )}
    </div>
  );
};
